import React, { useState, useEffect } from 'react';
import FitTable from '../components/FitTable';
import TournamentWrapper from '../components/TournamentWrapper'; // Adjust the path based on your file structure
import TournamentSchedulerPanel from '../components/TournamentSchedulerPanel';
import { useAuth0 } from '@auth0/auth0-react';


interface Tournament {
  tournament_id: number;
  tournament_name: string;
  start_date: string;
  end_date: string;
  tournament_status: string;
  tournament_team_enabled: boolean;
  type?: string;
  region?: string | null;
}

// Regions group majors together for the Insights "Titles" widget.
const REGIONS = ['North', 'East', 'West', 'Tremblant'];

const Majors: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedScheduler, setExpandedScheduler] = useState<number | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null);
  const [teamRoster, setTeamRoster] = useState<{ player_id: number; first_name: string; last_name: string; team: string | null }[]>([]);
  const [teamInputs, setTeamInputs] = useState<Record<number, string>>({});
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamHasOverride, setTeamHasOverride] = useState(false);
  const [teamBulk, setTeamBulk] = useState<Record<string, string>>({});
  const [filterStatus, setFilterStatus] = useState<'open' | 'closed' | 'all'>('open');
  const [filterType, setFilterType] = useState<'all' | 'League' | 'Ad-Hoc'>('all');
  const [filterSearch, setFilterSearch] = useState('');
  const APIURL = process.env.REACT_APP_API_URL;


  const fetchTournaments = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${APIURL}/tournaments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const responseData = await response.json();
      const formattedData = responseData
        .map((tournament: Tournament) => ({
          ...tournament,
          start_date: tournament.start_date.split('T')[0], // Format to 'YYYY-MM-DD'
          end_date: tournament.end_date ? tournament.end_date.split('T')[0] : '',
        }))
        .sort((a: Tournament, b: Tournament) =>
          (b.start_date || '').localeCompare(a.start_date || '')
        );
      setData(formattedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const deleteTournament = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this tournament?')) return;
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${APIURL}/tournaments/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        if (response.status === 409) {
          const errorData = await response.json();
          alert(errorData.error || 'Cannot delete tournament: associated rounds exist.');
        } else {
          throw new Error('Failed to delete tournament');
        }
        return;
      }
  
      fetchTournaments(); // Refresh the list after deletion
    } catch (error) {
      console.error('Error deleting tournament:', error);
      alert('Failed to delete tournament. Please try again.');
    }
  };
 
  
  const updateTournament = async (id: number, updates: Partial<Tournament>) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${APIURL}/tournaments/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      fetchTournaments(); // Refresh list after update
    } catch (error) {
      console.error('Error updating tournament:', error);
      alert('Failed to update tournament. Please try again.');
    }
  };
  

  const updateTournamentStatus = async (id: number, status: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${APIURL}/tournaments/${id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tournament_status: status }),
      });
      fetchTournaments(); // Refresh the list after status update
    } catch (error) {
      console.error('Error updating tournament status:', error);
      alert('Failed to update tournament status. Please try again.');
    }
  };


  const updateTournamentType = async (id: number, type: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${APIURL}/tournaments/${id}/type`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type }),
      });
      fetchTournaments(); // Refresh list after update
    } catch (error) {
      console.error('Error updating tournament type:', error);
      alert('Failed to update tournament type. Please try again.');
    }
  };

  // ── Per-major team-point override ──────────────────────────────────────────
  const openTeamPanel = async (id: number) => {
    if (expandedTeam === id) { setExpandedTeam(null); return; }
    setExpandedScheduler(null);
    setExpandedTeam(id);
    setTeamLoading(true);
    setTeamRoster([]);
    setTeamInputs({});
    setTeamHasOverride(false);
    setTeamBulk({});
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/tournaments/${id}/team-overrides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load team overrides');
      const data = await res.json();
      setTeamRoster(data.players || []);
      const inputs: Record<number, string> = {};
      (data.overrides || []).forEach((o: { player_id: number; points: number }) => {
        inputs[o.player_id] = String(o.points);
      });
      setTeamInputs(inputs);
      setTeamHasOverride((data.overrides || []).length > 0);
    } catch (err) {
      console.error('Error loading team overrides:', err);
      alert('Failed to load team points for this tournament.');
      setExpandedTeam(null);
    } finally {
      setTeamLoading(false);
    }
  };

  const putTeamOverrides = async (id: number, overrides: { player_id: number; points: number }[]) => {
    const token = await getAccessTokenSilently();
    const res = await fetch(`${APIURL}/tournaments/${id}/team-overrides`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ overrides }),
    });
    if (!res.ok) throw new Error('Failed to save team overrides');
  };

  const saveTeamOverrides = async (id: number) => {
    setTeamSaving(true);
    try {
      const overrides = Object.entries(teamInputs)
        .filter(([, v]) => v !== '' && v != null)
        .map(([pid, v]) => ({ player_id: Number(pid), points: Math.max(0, parseInt(v, 10) || 0) }));
      await putTeamOverrides(id, overrides);
      setTeamHasOverride(overrides.length > 0);
      alert('Team points saved.');
    } catch (err) {
      console.error('Error saving team overrides:', err);
      alert('Failed to save team points. Please try again.');
    } finally {
      setTeamSaving(false);
    }
  };

  const clearTeamOverrides = async (id: number) => {
    if (!window.confirm('Clear the team-point override for this major? It will revert to the automatic winning-team calculation.')) return;
    setTeamSaving(true);
    try {
      await putTeamOverrides(id, []);
      setTeamInputs({});
      setTeamHasOverride(false);
      alert('Override cleared.');
    } catch (err) {
      console.error('Error clearing team overrides:', err);
      alert('Failed to clear override. Please try again.');
    } finally {
      setTeamSaving(false);
    }
  };

  // Apply one value to every roster member of a team group (blank clears them).
  const applyToTeam = (teamName: string, raw: string) => {
    const v = raw.trim();
    setTeamInputs((prev) => {
      const next = { ...prev };
      teamRoster
        .filter((p) => (p.team === 'Lefties' || p.team === 'Righties' ? p.team : 'Other') === teamName)
        .forEach((p) => { if (v === '') delete next[p.player_id]; else next[p.player_id] = v; });
      return next;
    });
  };

  const renderTeamPanel = (id: number) => {
    const groupKey = (t: string | null) => (t === 'Lefties' || t === 'Righties' ? t : 'Other');
    const groups: Record<string, typeof teamRoster> = {};
    teamRoster.forEach((p) => { const k = groupKey(p.team); (groups[k] = groups[k] || []).push(p); });
    const order = ['Lefties', 'Righties', 'Other'].filter((g) => groups[g]?.length);
    const groupColor = (g: string) => (g === 'Lefties' ? '#1d4ed8' : g === 'Righties' ? '#b45309' : '#64748b');

    return (
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Tournament team points (override)
          {teamHasOverride && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 999, padding: '2px 9px', textTransform: 'uppercase' }}>
              Override active
            </span>
          )}
        </div>
        <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
          Assign the team points each player earned for this major — including members who didn’t play it. Use
          “Set all” to give a whole team the same amount. Any tagged player turns this into an override: the automatic
          team competition calculation is skipped for this tournament and only these points count. Leave everyone
          blank (or Clear) to use the automatic calculation.
        </p>
        {teamLoading ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>Loading roster…</div>
        ) : teamRoster.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>No league members found.</div>
        ) : (
          <>
            {order.map((g) => (
              <div key={g} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: groupColor(g) }}>
                    {g} <span style={{ color: '#94a3b8', fontWeight: 600 }}>· {groups[g].length}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                    <input
                      type="number" min={0} placeholder="pts"
                      value={teamBulk[g] ?? ''}
                      onChange={(e) => setTeamBulk((prev) => ({ ...prev, [g]: e.target.value }))}
                      style={{ width: 58, padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 6, textAlign: 'right', fontSize: 12 }}
                    />
                    <button type="button" onClick={() => applyToTeam(g, teamBulk[g] ?? '')}
                      style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                      Set all
                    </button>
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                  {groups[g].map((p) => (
                    <label key={p.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px' }}>
                      <span style={{ fontSize: 13, color: '#374151', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.first_name} {p.last_name}
                      </span>
                      <input
                        type="number" min={0} placeholder="—"
                        value={teamInputs[p.player_id] ?? ''}
                        onChange={(e) => setTeamInputs((prev) => ({ ...prev, [p.player_id]: e.target.value }))}
                        style={{ width: 64, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, textAlign: 'right', fontSize: 13 }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => saveTeamOverrides(id)} disabled={teamSaving}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: teamSaving ? 0.6 : 1 }}>
                {teamSaving ? 'Saving…' : 'Save team points'}
              </button>
              <button onClick={() => clearTeamOverrides(id)} disabled={teamSaving}
                style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Clear override
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchTournaments();
  }, [getAccessTokenSilently]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="majors-page">
      <div className="bga-page-header">
        <div>
          <h1 className="bga-h1">Major Tournaments</h1>
          <p className="bga-sub">Create and manage tournaments, schedules, and results.</p>
        </div>
        <TournamentWrapper onTournamentCreated={fetchTournaments} />
      </div>

      <div className="majors-filters">
        <input
          type="text"
          placeholder="Search by name…"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as 'open' | 'closed' | 'all')}>
          <option value="open">Status: Open</option>
          <option value="closed">Status: Closed</option>
          <option value="all">Status: All</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value as 'all' | 'League' | 'Ad-Hoc')}>
          <option value="all">Type: All</option>
          <option value="League">Type: League</option>
          <option value="Ad-Hoc">Type: Ad-Hoc</option>
        </select>
        {(() => {
          const visible = data.filter((row) => {
            if (filterStatus !== 'all' && row.tournament_status !== filterStatus) return false;
            if (filterType !== 'all' && row.type !== filterType) return false;
            if (filterSearch.trim() && !row.tournament_name.toLowerCase().includes(filterSearch.toLowerCase())) return false;
            return true;
          });
          return (
            <span className="majors-filter-count">
              {visible.length} of {data.length} shown
            </span>
          );
        })()}
      </div>

      <FitTable>
      <table className="majors-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Tournament Name</th>
            <th>Type</th>
            <th>Region</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th>Teams</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data
            .filter((row) => {
              if (filterStatus !== 'all' && row.tournament_status !== filterStatus) return false;
              if (filterType !== 'all' && row.type !== filterType) return false;
              if (filterSearch.trim() && !row.tournament_name.toLowerCase().includes(filterSearch.toLowerCase())) return false;
              return true;
            })
            .map((row) => {
            const isClosed = row.tournament_status === 'closed';
            return (
              <React.Fragment key={row.tournament_id}>
                <tr className="majors-row">
                  <td data-label="ID">{row.tournament_id}</td>
                  <td data-label="Name">
                    <input
                      type="text"
                      value={row.tournament_name}
                      onChange={(e) =>
                        updateTournament(row.tournament_id, { tournament_name: e.target.value })
                      }
                    />
                  </td>
                  <td data-label="Type">
                    <select
                      value={row.type || ''}
                      onChange={(e) => updateTournamentType(row.tournament_id, e.target.value)}
                    >
                      <option value="" disabled>Select..</option>
                      <option value="League">League</option>
                      <option value="Ad-Hoc">Ad-Hoc</option>
                    </select>
                  </td>
                  <td data-label="Region">
                    <select
                      value={row.region || ''}
                      onChange={(e) => updateTournament(row.tournament_id, { region: e.target.value || null })}
                    >
                      <option value="">— None —</option>
                      {REGIONS.map((rg) => (
                        <option key={rg} value={rg}>{rg}</option>
                      ))}
                    </select>
                  </td>
                  <td data-label="Start">
                    <input
                      type="date"
                      value={row.start_date}
                      onChange={(e) =>
                        updateTournament(row.tournament_id, { start_date: e.target.value })
                      }
                    />
                  </td>
                  <td data-label="End">
                    <input
                      type="date"
                      value={row.end_date}
                      onChange={(e) =>
                        updateTournament(row.tournament_id, { end_date: e.target.value })
                      }
                    />
                  </td>
                  <td data-label="Status">
                    <select
                      value={row.tournament_status}
                      onChange={(e) => updateTournamentStatus(row.tournament_id, e.target.value)}
                    >
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                  <td data-label="Teams">
                    <select
                      value={row.tournament_team_enabled ? 'true' : 'false'}
                      onChange={(e) =>
                        updateTournament(row.tournament_id, {
                          tournament_team_enabled: e.target.value === 'true',
                        })
                      }
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </td>
                  <td data-label="Actions">
                    <div className="actions-cell">
                      <button className="delete-btn" onClick={() => deleteTournament(row.tournament_id)}>
                        Delete
                      </button>
                      <button
                        disabled={isClosed}
                        title={isClosed ? 'Reopen the tournament to set up its schedule.' : ''}
                        onClick={() =>
                          setExpandedScheduler(
                            expandedScheduler === row.tournament_id ? null : row.tournament_id
                          )
                        }
                      >
                        {expandedScheduler === row.tournament_id ? '▲ Schedule' : '▼ Schedule'}
                      </button>
                      <button
                        title="Tag players with team points for this major (overrides the automatic team competition calculation)"
                        onClick={() => openTeamPanel(row.tournament_id)}
                      >
                        {expandedTeam === row.tournament_id ? '▲ Team Pts' : '▼ Team Pts'}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedScheduler === row.tournament_id && !isClosed && (
                  <tr className="scheduler-row">
                    <td colSpan={9} style={{ padding: '0 8px 12px 8px' }}>
                      {/* width:0 + min-width:100% keeps the panel from widening the table's min-content */}
                      <div style={{ width: 0, minWidth: '100%' }}>
                        <TournamentSchedulerPanel
                          tournamentId={row.tournament_id}
                          tournamentName={row.tournament_name}
                          startDate={row.start_date}
                          endDate={row.end_date}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                {expandedTeam === row.tournament_id && (
                  <tr className="scheduler-row">
                    <td colSpan={9} style={{ padding: '0 8px 12px 8px' }}>
                      <div style={{ width: 0, minWidth: '100%' }}>{renderTeamPanel(row.tournament_id)}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </FitTable>
    </div>
  );
};

export default Majors;
